import { useMemo } from 'react';
import { useTodos, type Todo } from '../../crm-todos/useData';

export interface TodosMonthAgg {
  count: number;
}

export interface TodosClientMonthRow {
  clientId: number | null;
  clientName: string;
  months: TodosMonthAgg[];
  totalCount: number;
}

function getCompletedMonthIndex(todo: Todo): number | null {
  const dateStr = todo.todoCompletedAt;
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.getMonth();
}

function getCompletedYear(todo: Todo): number | null {
  const dateStr = todo.todoCompletedAt;
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.getFullYear();
}

export const useTodosMonthlyReport = (year: number) => {
  const { todos, isLoading, isError } = useTodos();

  const todosInYear = useMemo(() => {
    return todos.filter(
      (t) => t.todoCompleted && t.todoCompletedAt && getCompletedYear(t) === year
    );
  }, [todos, year]);

  const data = useMemo((): TodosClientMonthRow[] => {
    const byClient: Record<
      string,
      { clientId: number | null; clientName: string; months: TodosMonthAgg[] }
    > = {};

    todosInYear.forEach((t) => {
      const mi = getCompletedMonthIndex(t);
      if (mi == null) return;
      const cid = t.clientId ?? 'none';
      const clientName =
        t.client?.clientName ?? (t.clientId ? `Client #${t.clientId}` : 'No client');
      if (!byClient[cid]) {
        byClient[cid] = {
          clientId: cid === 'none' ? null : Number(cid),
          clientName,
          months: Array.from({ length: 12 }, () => ({ count: 0 })),
        };
      }
      byClient[cid].months[mi].count += 1;
    });

    return Object.values(byClient).map((row) => {
      const totalCount = row.months.reduce((s, m) => s + m.count, 0);
      return {
        clientId: row.clientId,
        clientName: row.clientName,
        months: row.months,
        totalCount,
      };
    });
  }, [todosInYear]);

  return {
    data,
    isLoading,
    isError,
    todosInYear,
  };
};

export function getCompletedMonthIndexForTodo(todo: Todo): number | null {
  return getCompletedMonthIndex(todo);
}
